import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Text,
  VStack,
  useToast,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FiFileText, FiShoppingCart, FiClipboard, FiTrendingUp } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../../Utilities/networks';

const SalesDashboard = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    enquiries: 0,
    quotations: 0,
    proformaInvoices: 0,
    totalRevenue: 0,
  });
  const [recentEnquiries, setRecentEnquiries] = useState([]);
  const [recentQuotations, setRecentQuotations] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);

  useEffect(() => {
    fetchSalesData();
  }, []);

  const fetchSalesData = async () => {
    try {
      setIsLoading(true);

      // Fetch enquiries
      const enquiriesResponse = await axiosInstance.get('/api/sales/enquiries');
      const enquiries = enquiriesResponse.data.data || [];

      // Fetch quotations
      const quotationsResponse = await axiosInstance.get('/api/sales/quotations');
      const quotations = quotationsResponse.data.data || [];

      // Fetch proforma invoices
      const invoicesResponse = await axiosInstance.get('/api/sales/proforma-invoices');
      const invoices = invoicesResponse.data.data || [];

      // Calculate stats
      const totalRevenue = [...quotations, ...invoices]
        .reduce((sum, item) => sum + parseFloat(item.total || 0), 0);

      setStats({
        enquiries: enquiries.length,
        quotations: quotations.length,
        proformaInvoices: invoices.length,
        totalRevenue: totalRevenue,
      });

      // Get recent items (last 5)
      setRecentEnquiries(enquiries.slice(-5).reverse());
      setRecentQuotations(quotations.slice(-5).reverse());
      setRecentInvoices(invoices.slice(-5).reverse());

    } catch (error) {
      console.error('Error fetching sales data:', error);
      toast({
        title: 'Error Loading Data',
        description: 'Failed to load sales data. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateEnquiry = () => {
    navigate('/sales/enquiry');
  };

  const handleCreateQuotation = () => {
    navigate('/sales/quotation');
  };

  const handleCreateInvoice = () => {
    navigate('/sales/proforma-invoice');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Processed': return 'green';
      case 'Denied': return 'red';
      default: return 'orange';
    }
  };

  if (isLoading) {
    return (
      <Container maxW="7xl" py={8}>
        <VStack spacing={8} align="center" justify="center" minH="400px">
          <Spinner size="xl" color="blue.500" />
          <Text>Loading sales dashboard...</Text>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="7xl" py={8}>
      <VStack spacing={8} align="stretch">
        {/* Header */}
        <Box textAlign="center">
          <Heading size="xl" color="blue.600" mb={2}>
            Sales Management
          </Heading>
          <Text color="gray.600" fontSize="lg">
            Manage enquiries, quotations, and proforma invoices
          </Text>
        </Box>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <Heading size="md" color="gray.700">
              Quick Actions
            </Heading>
          </CardHeader>
          <CardBody>
            <HStack spacing={4} wrap="wrap">
              <Button
                leftIcon={<FiFileText />}
                colorScheme="blue"
                onClick={handleCreateEnquiry}
                size="lg"
              >
                New Enquiry
              </Button>
              <Button
                leftIcon={<FiShoppingCart />}
                colorScheme="green"
                onClick={handleCreateQuotation}
                size="lg"
              >
                New Quotation
              </Button>
              <Button
                leftIcon={<FiClipboard />}
                colorScheme="purple"
                onClick={handleCreateInvoice}
                size="lg"
              >
                New Proforma Invoice
              </Button>
            </HStack>
          </CardBody>
        </Card>

        {/* Statistics */}
        <Grid templateColumns={{ base: '1fr', md: '2fr 1fr 1fr' }} gap={6}>
          <GridItem>
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>Overview</StatLabel>
                  <StatNumber>{stats.quotations + stats.proformaInvoices}</StatNumber>
                  <StatHelpText>Total Documents</StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </GridItem>
          <GridItem>
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>Revenue</StatLabel>
                  <StatNumber>₹{stats.totalRevenue.toLocaleString('en-IN')}</StatNumber>
                  <StatHelpText>Total Value</StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </GridItem>
          <GridItem>
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>Conversion</StatLabel>
                  <StatNumber>
                    {stats.quotations + stats.proformaInvoices > 0
                      ? Math.round((stats.proformaInvoices / (stats.quotations + stats.proformaInvoices)) * 100)
                      : 0}%
                  </StatNumber>
                  <StatHelpText>Invoice Rate</StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </GridItem>
        </Grid>

        {/* Detailed Tabs */}
        <Card>
          <CardHeader>
            <Heading size="md" color="gray.700">
              Recent Activity
            </Heading>
          </CardHeader>
          <CardBody>
            <Tabs variant="enclosed" colorScheme="blue">
              <TabList>
                <Tab>Enquiries ({recentEnquiries.length})</Tab>
                <Tab>Quotations ({recentQuotations.length})</Tab>
                <Tab>Proforma Invoices ({recentInvoices.length})</Tab>
              </TabList>

              <TabPanels>
                {/* Enquiries Tab */}
                <TabPanel>
                  {recentEnquiries.length === 0 ? (
                    <Alert status="info">
                      <AlertIcon />
                      No enquiries yet. Create your first enquiry to get started.
                    </Alert>
                  ) : (
                    <VStack spacing={4} align="stretch">
                      {recentEnquiries.map((enquiry) => (
                        <Card key={enquiry.id} variant="outline">
                          <CardBody>
                            <VStack align="stretch" spacing={3}>
                              <Flex justify="space-between" align="center">
                                <Heading size="sm" color="blue.600">
                                  {enquiry.name}
                                </Heading>
                                <Text fontSize="sm" color="gray.500">
                                  {new Date(enquiry.created_at).toLocaleDateString()}
                                </Text>
                              </Flex>
                              <Text fontWeight="semibold">Requirements:</Text>
                              <Text>{enquiry.requirement}</Text>
                              <HStack>
                                <Text fontSize="sm">📧 {enquiry.email}</Text>
                                <Text fontSize="sm">📱 {enquiry.contact_number}</Text>
                              </HStack>
                            </VStack>
                          </CardBody>
                        </Card>
                      ))}
                    </VStack>
                  )}
                </TabPanel>

                {/* Quotations Tab */}
                <TabPanel>
                  {recentQuotations.length === 0 ? (
                    <Alert status="info">
                      <AlertIcon />
                      No quotations yet. Create your first quotation.
                    </Alert>
                  ) : (
                    <VStack spacing={4} align="stretch">
                      {recentQuotations.map((quotation) => (
                        <Card key={quotation.id} variant="outline">
                          <CardBody>
                            <VStack align="stretch" spacing={3}>
                              <Flex justify="space-between" align="center">
                                <Heading size="sm" color="green.600">
                                  {quotation.name}
                                </Heading>
                                <HStack>
                                  <Badge colorScheme={getStatusColor(quotation.status)}>
                                    {quotation.status}
                                  </Badge>
                                  <Text fontSize="sm" color="gray.500">
                                    {new Date(quotation.created_at).toLocaleDateString()}
                                  </Text>
                                </HStack>
                              </Flex>
                              <Text fontWeight="semibold">{quotation.model}</Text>
                              <HStack justify="space-between">
                                <Text>Quantity: {quotation.quantity}</Text>
                                <Text fontWeight="bold" color="green.500">
                                  ₹{parseFloat(quotation.total).toLocaleString('en-IN')}
                                </Text>
                              </HStack>
                            </VStack>
                          </CardBody>
                        </Card>
                      ))}
                    </VStack>
                  )}
                </TabPanel>

                {/* Proforma Invoices Tab */}
                <TabPanel>
                  {recentInvoices.length === 0 ? (
                    <Alert status="info">
                      <AlertIcon />
                      No proforma invoices yet. Create your first invoice.
                    </Alert>
                  ) : (
                    <VStack spacing={4} align="stretch">
                      {recentInvoices.map((invoice) => (
                        <Card key={invoice.id} variant="outline">
                          <CardBody>
                            <VStack align="stretch" spacing={3}>
                              <Flex justify="space-between" align="center">
                                <Heading size="sm" color="purple.600">
                                  {invoice.name}
                                </Heading>
                                <HStack>
                                  <Badge colorScheme={getStatusColor(invoice.status)}>
                                    {invoice.status}
                                  </Badge>
                                  <Text fontSize="sm" color="gray.500">
                                    {new Date(invoice.created_at).toLocaleDateString()}
                                  </Text>
                                </HStack>
                              </Flex>
                              <Text fontWeight="semibold">{invoice.model}</Text>
                              <HStack justify="space-between">
                                <Text>Quantity: {invoice.quantity}</Text>
                                <Text fontWeight="bold" color="purple.500">
                                  ₹{parseFloat(invoice.total).toLocaleString('en-IN')}
                                </Text>
                              </HStack>
                            </VStack>
                          </CardBody>
                        </Card>
                      ))}
                    </VStack>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          </CardBody>
        </Card>
      </VStack>
    </Container>
  );
};

export default SalesDashboard;
